import { z } from "zod";
import { Id } from "../common/ids.js";
import { Money } from "../common/money.js";
import { Timestamp } from "../common/time.js";
import { defineRoute } from "../http/route.js";
import { JournalKind, LedgerAccountKind } from "../payment/ledger.routes.js";
import { ChangeOrderView } from "./change-order.routes.js";
import { OfflinePaymentView } from "./offline-payment.routes.js";
import { OrderDocumentView } from "./order.routes.js";
import { OrderSummary, OrderView } from "./order.view.js";
import { ShipmentView } from "./shipment.routes.js";
/**
 * Support's order lookup and read-only order view (ADM-5, card 079). Nothing here changes an order; every call is
 * audited as an operator's (`ops.call`). Message content is not part of it: it takes a reason and a grant.
 */
const Support = { surface: "operator", auth: "operator", permission: "ops:support.read" };
export const OpsOrderSummary = OrderSummary.omit({ side: true }).meta({ id: "OpsOrderSummary" });
/** One movement the ledger recorded for the order, with its two sides or more. */
export const OpsLedgerJournal = z
    .object({
    id: Id,
    kind: JournalKind.describe("`external_offline`: a bank transfer straight to the seller, outside Airwallex"),
    occurredAt: Timestamp,
    scheduleItemId: Id.nullable(),
    providerRef: z.string().nullable(),
    externalReference: z.string().nullable().describe("The payer's bank reference, for a bank transfer"),
    entries: z.array(z.object({
        account: LedgerAccountKind,
        enterpriseId: Id.nullable().describe("Null for the platform's accounts"),
        direction: z.enum(["debit", "credit"]),
        amount: Money,
    })),
})
    .meta({ id: "OpsLedgerJournal" });
/** Who is in the order's deal conversation; never what they wrote. */
export const OpsConversationOutline = z
    .object({
    id: Id,
    createdAt: Timestamp,
    participants: z.array(z.object({
        userId: Id,
        displayName: z.string(),
        enterpriseId: Id,
        role: z.enum(["owner", "admin", "member"]),
        status: z.enum(["active", "left"]),
        joinedAt: Timestamp,
        leftAt: Timestamp.nullable(),
    })),
})
    .meta({ id: "OpsConversationOutline" });
export const OpsOrderView = OrderView.omit({ side: true, actions: true })
    .extend({
    transfers: z
        .array(OfflinePaymentView)
        .describe("Every transfer reported for the order, newest first; bank account details are never shown"),
    shipments: z.array(ShipmentView.omit({ actions: true })),
    changeOrders: z.array(ChangeOrderView.omit({ actions: true })),
    documents: z.array(OrderDocumentView).describe("The order sheets as issued, newest first"),
    ledger: z.array(OpsLedgerJournal).describe("What the ledger recorded for the order, oldest first"),
    conversation: OpsConversationOutline.nullable(),
})
    .meta({ id: "OpsOrderView" });
export const listOpsOrders = defineRoute({
    ...Support,
    id: "ops.orders.list",
    method: "GET",
    path: "/v1/ops/orders",
    marks: [],
    summary: "Find orders, newest first: `q` is an order number (`ORD-2026-000123` or `2026-000123`) or a transfer's bank reference; `enterpriseId` is either party",
    prd: ["ADM-5"],
    request: {
        query: z.object({
            q: z.string().trim().min(1).max(100).optional(),
            enterpriseId: Id.optional(),
            limit: z.coerce.number().int().min(1).max(50).default(50),
        }),
    },
    responses: { 200: { description: "Orders", schema: z.object({ items: z.array(OpsOrderSummary) }) } },
    errors: ["AUTH_REQUIRED", "PERMISSION_DENIED", "VALIDATION_FAILED"],
});
export const getOpsOrder = defineRoute({
    ...Support,
    id: "ops.orders.get",
    method: "GET",
    path: "/v1/ops/orders/{orderId}",
    marks: [],
    summary: "One order as both parties see it, with its transfers, shipments, change orders, order sheets, ledger journals, and its deal conversation's participants; read-only",
    prd: ["ADM-5"],
    request: { params: z.object({ orderId: Id }) },
    responses: { 200: { description: "The order", schema: OpsOrderView } },
    errors: ["AUTH_REQUIRED", "PERMISSION_DENIED", "NOT_FOUND"],
});
export const opsOrderRoutes = [listOpsOrders, getOpsOrder];
//# sourceMappingURL=ops-order.routes.js.map